import axios from "axios";
import { API_BASE_URL, IS_DEVELOPMENT, DEV_CONFIG } from '../config';
import { detectRegion } from '../config/regionalConfig';

// Types
export interface AdminUser {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role: string;
  isAdmin: boolean;
  phone?: string;
  assignedRegion?: string;
  hub?: string;
  city?: string;
  location?: string;
  assignedCity?: string;
  assignedHub?: string;
}

export interface AuthResponse {
  token?: string;
  user?: AdminUser;
  requires2FA?: boolean;
  tempToken?: string;
  email?: string;
}

export class AuthError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = "AuthError";
    this.status = status;
  }
}

// Create a dedicated axios instance for auth requests
const authApi = axios.create({
  baseURL: API_BASE_URL, // Use dynamic base URL from config
  timeout: 10000,
  headers: {
    "Content-Type": "application/json"
  }
});



// Add request interceptor for debugging and token handling
authApi.interceptors.request.use(config => {
  const region = detectRegion();
  config.headers['x-region'] = region;

  // Add token to request if available and not already set
  const token = getToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Add response interceptor for better error handling
authApi.interceptors.response.use(
  response => response,
  error => {
    if (DEV_CONFIG.enableDebugLogging) {
      console.error('Auth API Error:', error.response?.status, error.response?.data);
    }
    return Promise.reject(error);
  }
);

/**
 * Authenticate admin user
 */
export const login = async (email: string, password: string): Promise<AuthResponse> => {
  try {


    // Clear any existing tokens
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");

    // Attempt login with admin endpoint


    const response = await authApi.post<any>("/admin/auth/login", {
      email,
      password
    });

    const apiResponse = response.data as any;

    // Handle 2FA challenge requirement
    if (apiResponse && apiResponse.requires2FA) {
      return {
        requires2FA: true,
        tempToken: apiResponse.tempToken,
        email: apiResponse.email
      };
    }

    // Validate the response
    if (!apiResponse || !apiResponse.token) {
      throw new Error('Invalid response from authentication server');
    }

    const user = apiResponse.data?.user || apiResponse.user;
    if (!user) {
      throw new Error('User data missing in response');
    }

    return processSuccessfulLogin({
      token: apiResponse.token,
      user: user
    });

  } catch (error: any) {
    console.error('[AuthService] Login error:', error);
    throw new AuthError(
      error.response?.data?.message || error.message || 'Login failed',
      error.response?.status
    );
  }
};

/**
 * Verify 2FA challenge code
 */
export const verify2FA = async (tempToken: string, code: string): Promise<AuthResponse> => {
  try {
    const response = await authApi.post<any>("/admin/auth/2fa/verify", {
      tempToken,
      code
    }, {
      headers: {
        Authorization: `Bearer ${tempToken}`
      }
    });

    const apiResponse = response.data as any;
    if (!apiResponse || !apiResponse.token) {
      throw new Error('Invalid verification response from authentication server');
    }

    const user = apiResponse.data?.user || apiResponse.user;
    return processSuccessfulLogin({
      token: apiResponse.token,
      user
    });
  } catch (error: any) {
    console.error('[AuthService] 2FA verification error:', error);
    throw new AuthError(
      error.response?.data?.message || error.message || '2FA verification failed',
      error.response?.status
    );
  }
};

/**
 * Generate 2FA secret and QR code for enrollment
 */
export const setup2FA = async (): Promise<{ secret: string; otpauthUrl: string; qrCode: string; recoveryCodes: string[] }> => {
  const response = await authApi.post("/admin/auth/2fa/setup");
  return response.data.data;
};

/**
 * Confirm 2FA enrollment with 6-digit verification code
 */
export const confirm2FA = async (code: string, secret: string, recoveryCodes: string[]): Promise<any> => {
  const response = await authApi.post("/admin/auth/2fa/confirm", {
    code,
    secret,
    recoveryCodes
  });
  return response.data;
};

/**
 * Disable 2FA with current password and TOTP code
 */
export const disable2FA = async (password: string, code: string): Promise<any> => {
  const response = await authApi.post("/admin/auth/2fa/disable", {
    password,
    code
  });
  return response.data;
};

/**
 * Process a successful login response
 * @param data The login response data
 * @returns The processed login response
 */
function processSuccessfulLogin(data: { token: string; user: AdminUser }): AuthResponse {
  const token = data.token;
  localStorage.setItem("admin_token", token);
  const user = { ...data.user };
  if (user.name && (!user.firstName || !user.lastName)) {
    const parts = user.name.trim().split(/\s+/);
    user.firstName = parts[0] || '';
    user.lastName = parts.slice(1).join(' ') || '';
  }
  localStorage.setItem("admin_user", JSON.stringify(user));

  return {
    token,
    user
  };
}

/**
 * Create a mock login response for development
 * @param email The email to use for the mock user
 * @returns A mock login response
 */
function createMockLoginResponse(email: string): AuthResponse {
  console.warn('Creating mock admin login response for development');

  const mockToken = 'dev-admin-token-' + Date.now();
  const mockUser: AdminUser = {
    id: 'dev-admin-id',
    email: email,
    firstName: 'Admin',
    lastName: 'User',
    role: 'admin',
    isAdmin: true,
    phone: '+1234567890'
  };

  // Store mock auth data with raw token (no Bearer prefix)
  localStorage.setItem("admin_token", mockToken);
  localStorage.setItem("admin_user", JSON.stringify(mockUser));





  return {
    token: mockToken,
    user: mockUser
  };
}

/**
 * Refresh token
 * @param oldToken The old token to refresh
 * @returns The new token
 */
export const refreshToken = async (oldToken: string): Promise<{ token: string }> => {
  try {
    const response = await authApi.post<{ token: string }>("/admin/auth/refresh", {}, {
      headers: {
        Authorization: `Bearer ${oldToken}`
      }
    });

    const { token } = response.data;
    localStorage.setItem('admin_token', token);

    return { token };
  } catch (error: any) {
    if (error.response) {
      throw new AuthError(error.response.data.message || 'Token refresh failed');
    }
    throw new AuthError('Network error occurred during token refresh');
  }
};

/**
 * Log out admin user
 */
export const logout = (): void => {

  localStorage.removeItem("admin_token");
  localStorage.removeItem("admin_user");

};

/**
 * Get current admin user from localStorage
 */
export const getCurrentUser = (): AdminUser | null => {
  const userJson = localStorage.getItem("admin_user");
  if (!userJson) return null;

  try {
    return JSON.parse(userJson) as AdminUser;
  } catch (error) {
    console.error("Error parsing user data:", error);
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem("admin_token");
};

/**
 * Get admin token
 * @returns The raw token string (without 'Bearer ' prefix) or null if not found
 */
export const getToken = (): string | null => {
  const adminToken = localStorage.getItem("admin_token");
  if (!adminToken) {

    return null;
  }

  // Clean the token
  let cleanToken = adminToken.trim();
  if (cleanToken.startsWith('Bearer ')) {
    cleanToken = cleanToken.substring(7);
  }

  return cleanToken;
};

/**
 * Format token for API requests
 * Adds the 'Bearer ' prefix required for Authorization headers
 * @returns The token with 'Bearer ' prefix for API requests
 */
export const getFormattedToken = (): string | null => {
  const token = getToken();
  if (!token) return null;

  // Always ensure we have a clean token first (no 'Bearer ' prefix)
  const cleanToken = token.startsWith('Bearer ') ? token.substring(7).trim() : token.trim();

  // Then add the 'Bearer ' prefix consistently
  return `Bearer ${cleanToken}`;
};

/**
 * Fetch current admin user from API
 */
export const fetchCurrentUser = async (): Promise<AdminUser> => {
  try {
    const token = getFormattedToken();
    if (!token) {
      console.error('No authentication token found');
      throw new AuthError("No authentication token", 401);
    }


    const response = await authApi.get<AdminUser>("/admin/auth/me", {
      headers: { Authorization: token }
    });


    return response.data;
  } catch (error: any) {
    console.error("Error fetching current user:", error);

    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message;
      console.error(`Axios error fetching user: ${status} - ${message}`);

      throw new AuthError(message, status);
    }

    throw new AuthError("Failed to fetch user data");
  }
};
